import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { companiesApi, type CompanyFilter } from "./api";
import { IndustryFilterSelect } from "./IndustryFilterSelect";
import { Button, Card, Input, PageTitle } from "../../components/ui";
import { QueryView } from "../../components/states";

export function PublicCompanyListPage() {
  const [filter, setFilter] = useState<CompanyFilter>({});
  const [draft, setDraft] = useState({ q: "", area: "", industryCode: "" });

  const query = useQuery({
    queryKey: ["companies", "public", filter],
    queryFn: () => companiesApi.listPublic(filter),
  });

  return (
    <div className="space-y-8">
      {/* ヒーロー */}
      <section className="rounded-soft bg-white/70 px-6 py-10 text-center shadow-soft">
        <p className="text-sm font-bold text-brand">中学生の学びと、まちの企業をつなぐ</p>
        <h1 className="mt-2 text-3xl font-extrabold text-brand-dark md:text-4xl">
          職場体験の受入先をさがそう
        </h1>
        <p className="mt-3 text-sm text-slate-500">
          苫小牧市内の受入企業を、業種やエリアから見つけられます。
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <Input
            placeholder="キーワード（企業名・仕事内容）"
            value={draft.q}
            onChange={(e) => setDraft({ ...draft, q: e.target.value })}
            className="!w-64"
          />
          <Input
            placeholder="エリア"
            value={draft.area}
            onChange={(e) => setDraft({ ...draft, area: e.target.value })}
            className="!w-36"
          />
          <IndustryFilterSelect
            value={draft.industryCode}
            onChange={(code) => setDraft({ ...draft, industryCode: code })}
          />
          <Button onClick={() => setFilter({ ...draft, page: 1 })}>さがす</Button>
        </div>
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          <Link to="/map">
            <Button variant="ghost">🗺 地図でさがす</Button>
          </Link>
          <Link to="/map/search">
            <Button variant="ghost">📍 地図から範囲検索</Button>
          </Link>
        </div>
      </section>

      <PageTitle title="受入企業一覧" />
      <QueryView
        query={query}
        emptyWhen={(d) => d.items.length === 0}
        emptyMessage="条件に合う企業が見つかりませんでした。"
      >
        {(data) => (
          <>
            <div className="grid gap-4 md:grid-cols-2">
              {data.items.map((c) => (
                <Link key={c.id} to={`/companies/${c.id}`}>
                  <Card className="h-full transition hover:-translate-y-0.5">
                    <h3 className="text-lg font-extrabold text-brand-dark">{c.name}</h3>
                    <p className="mt-1 text-xs text-slate-400">
                      {c.area}・{c.industryLabel}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {c.jobTypes.map((j) => (
                        <span
                          key={j}
                          className="rounded-pill bg-sky-light px-2.5 py-0.5 text-xs font-bold text-brand-dark"
                        >
                          {j}
                        </span>
                      ))}
                    </div>
                    <p className="mt-3 line-clamp-2 text-sm text-slate-600">{c.description}</p>
                  </Card>
                </Link>
              ))}
            </div>
            <p className="mt-4 text-center text-xs text-slate-400">全 {data.total} 件</p>
          </>
        )}
      </QueryView>

      <Card className="bg-sky-soft text-center">
        <p className="text-sm font-bold text-brand-dark">受入企業として参加しませんか？</p>
        <p className="mt-1 text-xs text-slate-500">
          若い人材の受け入れ・研修の練習に。登録は無料です。
        </p>
        <div className="mt-4">
          <Link to="/register/company">
            <Button>受入企業として登録する</Button>
          </Link>
        </div>
      </Card>
    </div>
  );
}
